import { useBlocker, type BlockerFunction } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";

export const useFormBlocker = (shouldBlockNavigation: boolean) => {
  // Stable predicate identity: read the latest value via a ref so the function
  // passed to useBlocker doesn't change every render. Reduces effect churn in
  // react-router-dom 6.26 and avoids a class of timing surprises around its
  // internal registration effects.
  const shouldBlockRef = useRef(shouldBlockNavigation);
  shouldBlockRef.current = shouldBlockNavigation;

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => shouldBlockRef.current && currentLocation.pathname !== nextLocation.pathname,
      [],
    ),
  );

  const isBlocked = blocker.state === "blocked";
  const [isModalOpen, setModalOpen] = useState(false);

  const handleCloseModal = () => {
    setModalOpen(false);
    if (isBlocked) blocker.reset();
  };

  const handleProceed = () => {
    setModalOpen(false);
    if (isBlocked) blocker.proceed();
  };

  useEffect(() => {
    if (isBlocked) setModalOpen(true);
  }, [isBlocked]);

  // Defensive unmount: if this hook's blocker is still in "blocked" state when
  // the form unmounts (e.g. parent re-renders the tree away while the modal is
  // open), release it so the route doesn't stay locked. A ref keeps cleanup
  // from running on every blocker reference change.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    return () => {
      if (blockerRef.current.state === "blocked") blockerRef.current.reset();
    };
  }, []);

  return {
    isModalOpen,
    handleCloseModal,
    handleProceed,
  };
};
